import React from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import AudioFile from '@mui/icons-material/AudioFile';
import Delete from '@mui/icons-material/Delete';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Error from '@mui/icons-material/Error';
import Upload from '@mui/icons-material/Upload';
import HourglassEmpty from '@mui/icons-material/HourglassEmpty';
import { FileWithMetadata } from '../../types/Upload';

interface FileListProps {
  files: FileWithMetadata[];
  /** Omit for a read-only list (e.g. in-flight uploads) — the remove button is then disabled. */
  onRemoveFile?: (fileId: string) => void;
  disabled?: boolean;
}

export const FileList: React.FC<FileListProps> = ({ files, onRemoveFile, disabled = false }) => {
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (status: FileWithMetadata['status']) => {
    switch (status) {
      case 'pending':
        return <AudioFile color="action" />;
      case 'uploading':
        return <Upload color="primary" />;
      case 'completed':
        return <CheckCircle color="success" />;
      case 'verifying':
        return <HourglassEmpty color="warning" />;
      case 'error':
        return <Error color="error" />;
      default:
        return <AudioFile color="action" />;
    }
  };

  const getStatusChip = (file: FileWithMetadata) => {
    switch (file.status) {
      case 'pending':
        return <Chip label="Pending" size="small" variant="outlined" />;
      case 'uploading':
        return <Chip label="Uploading" size="small" color="primary" variant="outlined" />;
      case 'completed':
        return <Chip label="Completed" size="small" color="success" variant="outlined" />;
      case 'verifying':
        return <Chip label="Verifying…" size="small" color="info" variant="outlined" />;
      case 'error':
        return <Chip label="Error" size="small" color="error" variant="outlined" />;
      default:
        return null;
    }
  };

  if (files.length === 0) {
    return null;
  }

  return (
    <Paper elevation={1} sx={{ mt: 3 }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6">
          Selected Files ({files.length})
        </Typography>
      </Box>
      
      <List>
        {files.map((fileData) => (
          <ListItem key={fileData.id} divider>
            <ListItemIcon>
              {getStatusIcon(fileData.status)}
            </ListItemIcon>
            
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" noWrap sx={{ flex: 1 }} component="span">
                    {fileData.file.name}
                  </Typography>
                  {getStatusChip(fileData)}
                </Box>
              }
              secondary={
                <Box>
                  <Typography variant="caption" color="text.secondary" component="span">
                    {formatFileSize(fileData.file.size)} • {fileData.file.type}
                  </Typography>
                  
                  {fileData.status === 'uploading' && (
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={fileData.progress} 
                        sx={{ height: 4, borderRadius: 2 }}
                      />
                      <Typography variant="caption" color="text.secondary" component="span">
                        {fileData.progress}%
                      </Typography>
                    </Box>
                  )}
                  
                  {fileData.status === 'verifying' && (
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress sx={{ height: 4, borderRadius: 2 }} />
                      <Typography variant="caption" color="text.secondary" component="span">
                        Confirming with server…
                      </Typography>
                    </Box>
                  )}

                  {fileData.status === 'error' && fileData.errorMessage && (
                    <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }} component="span">
                      {fileData.errorMessage}
                    </Typography>
                  )}
                </Box>
              }
              secondaryTypographyProps={{ component: 'div' }}
            />
            
            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                onClick={() => onRemoveFile?.(fileData.id)}
                disabled={disabled || !onRemoveFile || fileData.status === 'uploading'}
                size="small"
              >
                <Delete />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};